import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type UseUnsavedChangesGuardOptions = {
  when: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

type PendingAction = { type: 'navigate'; to: string } | { type: 'back' };

function isInternalAppLink(anchor: HTMLAnchorElement): string | null {
  if (anchor.hasAttribute('download') || anchor.target === '_blank') return null;

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return null;

    const next = `${url.pathname}${url.search}${url.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next === current) return null;

    return next;
  } catch {
    return null;
  }
}

export function useUnsavedChangesGuard({
  when,
  title = 'Leave without saving?',
  description = 'You have unsaved changes. If you leave now, your progress will be lost.',
  confirmLabel = 'Leave',
  cancelLabel = 'Stay',
}: UseUnsavedChangesGuardOptions) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef<PendingAction | null>(null);
  const whenRef = useRef(when);
  const bypassRef = useRef(false);

  useEffect(() => {
    whenRef.current = when;
  }, [when]);

  useEffect(() => {
    if (!when) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  useEffect(() => {
    if (!when) return;

    const onClick = (event: MouseEvent) => {
      if (bypassRef.current || !whenRef.current) return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.('a');
      if (!anchor) return;

      const to = isInternalAppLink(anchor);
      if (!to) return;

      event.preventDefault();
      event.stopPropagation();
      pendingActionRef.current = { type: 'navigate', to };
      setOpen(true);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [when]);

  useEffect(() => {
    if (!when) return;

    window.history.pushState({ __unsavedGuard: true }, '');

    const onPopState = () => {
      if (bypassRef.current || !whenRef.current) return;

      window.history.pushState({ __unsavedGuard: true }, '');
      pendingActionRef.current = { type: 'back' };
      setOpen(true);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [when]);

  const handleStay = useCallback(() => {
    pendingActionRef.current = null;
    setOpen(false);
  }, []);

  const handleLeave = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setOpen(false);

    bypassRef.current = true;
    whenRef.current = false;

    if (action?.type === 'navigate') {
      navigate(action.to);
    } else if (action?.type === 'back') {
      window.history.go(-2);
    }

    window.setTimeout(() => {
      bypassRef.current = false;
    }, 0);
  }, [navigate]);

  const dialog = (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleStay();
      }}
    >
      <AlertDialogContent className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="rounded-xl border-0 bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
            onClick={handleStay}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleLeave}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { dialog };
}
